/**
 * Export utilities for Analytics Dashboard
 * Supports CSV and PDF export
 */

/**
 * Convert data array to CSV string
 */
export function arrayToCSV(data, columns) {
  if (!data || !data.length) return '';
  
  const headers = columns.map(c => c.label || c.key);
  const keys = columns.map(c => c.key);
  
  const rows = data.map(row => 
    keys.map(key => {
      const val = row[key];
      // Handle different types
      if (val === null || val === undefined) return '';
      if (typeof val === 'number') return val;
      if (typeof val === 'string' && val.includes(',')) return `"${val}"`;
      return val;
    }).join(',')
  );
  
  return [headers.join(','), ...rows].join('\n');
}

/**
 * Download string as file
 */
export function downloadFile(content, filename, mimeType = 'text/csv') {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Export data as CSV
 */
export function exportToCSV(data, columns, filename) {
  const csv = arrayToCSV(data, columns);
  if (!csv) {
    console.warn('No data to export');
    return false;
  }
  downloadFile(csv, `${filename}.csv`, 'text/csv;charset=utf-8');
  return true;
}

/**
 * Format currency for export
 */
export function formatCurrency(value, currency = 'INR') {
  if (value === null || value === undefined) return '';
  const symbols = { INR: '₹', USD: '$', AED: 'AED ', GBP: '£' };
  return `${symbols[currency] || ''}${Number(value).toLocaleString('en-IN')}`;
}

/**
 * Format percentage for export
 */
export function formatPercent(value) {
  if (value === null || value === undefined) return '';
  return `${Number(value).toFixed(1)}%`;
}

/**
 * Generate print-friendly HTML for PDF export
 */
export function generatePrintHTML(title, sections) {
  const styles = `
    <style>
      @page { margin: 1.5cm; size: A4; }
      body { font-family: 'Segoe UI', system-ui, sans-serif; color: #1B2A4A; font-size: 11px; line-height: 1.4; }
      h1 { font-size: 20px; font-weight: 600; margin-bottom: 8px; color: #1B2A4A; }
      h2 { font-size: 14px; font-weight: 600; margin: 16px 0 8px; color: #1B2A4A; border-bottom: 1px solid #E5E7EB; padding-bottom: 4px; }
      .header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px; padding-bottom: 12px; border-bottom: 2px solid #C5A55A; }
      .logo { font-size: 18px; font-weight: 700; color: #1B2A4A; }
      .date { font-size: 10px; color: #6B7280; }
      .summary-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; margin-bottom: 16px; }
      .summary-card { background: #F9FAFB; border-radius: 6px; padding: 10px; }
      .summary-label { font-size: 9px; color: #6B7280; text-transform: uppercase; letter-spacing: 0.5px; }
      .summary-value { font-size: 16px; font-weight: 600; color: #1B2A4A; }
      table { width: 100%; border-collapse: collapse; margin: 8px 0; }
      th { background: #F3F4F6; padding: 6px 8px; text-align: left; font-weight: 600; font-size: 10px; text-transform: uppercase; color: #6B7280; }
      td { padding: 6px 8px; border-bottom: 1px solid #E5E7EB; }
      tr:nth-child(even) { background: #F9FAFB; }
      .text-right { text-align: right; }
      .text-green { color: #059669; }
      .text-red { color: #DC2626; }
      .footer { margin-top: 24px; padding-top: 12px; border-top: 1px solid #E5E7EB; font-size: 9px; color: #6B7280; text-align: center; }
    </style>
  `;
  
  return `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="utf-8">
      <title>${title}</title>
      ${styles}
    </head>
    <body>
      <div class="header">
        <div class="logo">Herringbone & Sui</div>
        <div class="date">Generated: ${new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}</div>
      </div>
      <h1>${title}</h1>
      ${sections.join('\n')}
      <div class="footer">
        Confidential — Herringbone & Sui Analytics Report
      </div>
    </body>
    </html>
  `;
}

/**
 * Open print dialog with HTML content
 */
export function printHTML(html) {
  const printWindow = window.open('', '_blank');
  if (!printWindow) {
    alert('Please allow popups to export PDF');
    return false;
  }
  printWindow.document.write(html);
  printWindow.document.close();
  printWindow.onload = () => {
    printWindow.print();
  };
  return true;
}

/**
 * Generate summary cards HTML
 */
export function generateSummaryCardsHTML(cards) {
  return `
    <div class="summary-grid">
      ${cards.map(c => `
        <div class="summary-card">
          <div class="summary-label">${c.label}</div>
          <div class="summary-value">${c.value}</div>
        </div>
      `).join('')}
    </div>
  `;
}

/**
 * Generate table HTML
 */
export function generateTableHTML(title, columns, data) {
  if (!data || !data.length) {
    return `<h2>${title}</h2><p style="color: #6B7280; font-style: italic;">No data available</p>`;
  }
  
  return `
    <h2>${title}</h2>
    <table>
      <thead>
        <tr>
          ${columns.map(c => `<th class="${c.align === 'right' ? 'text-right' : ''}">${c.label}</th>`).join('')}
        </tr>
      </thead>
      <tbody>
        ${data.slice(0, 20).map(row => `
          <tr>
            ${columns.map(c => {
              let val = row[c.key];
              if (c.format === 'currency') val = formatCurrency(val);
              if (c.format === 'percent') val = formatPercent(val);
              return `<td class="${c.align === 'right' ? 'text-right' : ''} ${c.colorClass ? c.colorClass(row) : ''}">${val ?? ''}</td>`;
            }).join('')}
          </tr>
        `).join('')}
      </tbody>
    </table>
    ${data.length > 20 ? `<p style="color: #6B7280; font-size: 9px;">Showing 20 of ${data.length} rows</p>` : ''}
  `;
}
